/**
 * FormButton Component
 * Reusable button component with loading states and consistent styling
 * Optimized for form submissions with proper accessibility
 */

import { type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { LoadingSpinner } from './LoadingSpinner';

export interface FormButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  loadingText?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
}

const variants = {
  primary:
    'bg-sky-600 hover:bg-sky-700 text-white border-transparent focus:ring-sky-500',
  secondary:
    'bg-gray-600 hover:bg-gray-700 text-white border-transparent focus:ring-gray-500',
  outline:
    'bg-white hover:bg-gray-50 text-gray-700 border-gray-300 focus:ring-sky-500',
};

const sizes = {
  sm: 'px-3 py-2 text-sm min-h-[40px]',
  md: 'px-4 py-3 text-sm min-h-[44px]',
  lg: 'px-6 py-4 text-base min-h-[48px]',
};

export function FormButton({
  isLoading = false,
  loadingText,
  variant = 'primary',
  size = 'md',
  disabled,
  className,
  children,
  type = 'button',
  ...props
}: FormButtonProps) {
  const isDisabled = disabled || isLoading;

  return (
    <button
      type={type}
      disabled={isDisabled}
      className={cn(
        'relative flex justify-center items-center font-medium rounded-md border focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200',
        // Enhanced focus indicators for high contrast
        'focus:ring-2 focus:ring-offset-2 high-contrast:focus:ring-offset-4 high-contrast:focus:ring-4',
        variants[variant],
        sizes[size],
        'disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400 disabled:opacity-60',
        // High contrast support
        'high-contrast:border-2 high-contrast:font-bold',
        'w-full', // Full width for form buttons
        className
      )}
      aria-busy={isLoading}
      {...props}
    >
      {isLoading && <LoadingSpinner className="mr-2" />}
      {isLoading ? loadingText || 'Loading...' : children}
    </button>
  );
}
